import React from "react";
import { useCustom, useCreate, useUpdate } from "@refinedev/core";
import { Form, Input, Button, Card, Spin, message } from "antd";
import { MY_ORGANIZATION_QUERY, CREATE_ORGANIZATION_MUTATION, UPDATE_ORGANIZATION_MUTATION } from "./queries";
import { Organization } from "./interface";

export const OrganizationSettings: React.FC = () => {
  const [form] = Form.useForm<Organization>();

  const { data, isLoading, refetch } = useCustom<{ myOrganization: Organization | null }>({
    url: "",
    method: "get",
    meta: { gqlQuery: MY_ORGANIZATION_QUERY },
  });

  const { mutate: createOrg, isLoading: isCreating } = useCreate();
  const { mutate: updateOrg, isLoading: isUpdating } = useUpdate();

  const organization = data?.data?.myOrganization;

  React.useEffect(() => {
    if (organization) {
      form.setFieldsValue(organization);
    }
  }, [organization, form]);

  const onFinish = async (values: Organization) => {
    try {
      if (organization) {
        await updateOrg(
          {
            resource: "organization",
            id: organization.id,
            values,
            meta: { gqlMutation: UPDATE_ORGANIZATION_MUTATION },
          },
          {
            onSuccess: () => {
              message.success("Organization updated successfully");
              refetch();
            },
            onError: () => message.error("Failed to update organization"),
          }
        );
      } else {
        await createOrg(
          {
            resource: "organization",
            values,
            meta: { gqlMutation: CREATE_ORGANIZATION_MUTATION },
          },
          {
            onSuccess: () => {
              message.success("Organization created successfully");
              refetch();
            },
            onError: () => message.error("Failed to create organization"),
          }
        );
      }
    } catch {
      message.error("Failed to save organization");
    }
  };

  if (isLoading) {
    return <Spin style={{ display: "block", marginTop: 100 }} />;
  }

  return (
    <Card title="Organization Settings">
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        initialValues={{ name: "", about: "", tinNo: "", serviceIndustry: "" }}
        style={{ maxWidth: 600 }}
      >
        <Form.Item
          label="Organization Name"
          name="name"
          rules={[{ required: true, message: "Please enter the organization name" }]}
        >
          <Input placeholder="Enter organization name" />
        </Form.Item>
        <Form.Item label="About" name="about">
          <Input.TextArea rows={3} placeholder="Brief description about the organization" />
        </Form.Item>
        <Form.Item label="TIN No." name="tinNo">
          <Input placeholder="Enter tax identification number" />
        </Form.Item>
        <Form.Item label="Service Industry" name="serviceIndustry">
          <Input placeholder="Enter service industry" />
        </Form.Item>
        <Form.Item>
          <Button type="primary" htmlType="submit" loading={isCreating || isUpdating}>
            {organization ? "Update" : "Create"}
          </Button>
        </Form.Item>
      </Form>
    </Card>
  );
};